/**
 *  GameScene
 *
 *  Created by Legman on 21/02/2017.
 *
 */

"use strict";

import Actor        from "./Actor";
import Audio        from "./Audio";
import AudioParams  from "./AudioParams";
import Common       from "./Common";
import GummyBear    from "./GummyBear";
import Hud          from "./Hud";
import Monkey       from "./Monkey";
import PhysicsActor from "./PhysicsActor";
import Replicator   from "./Replicator";
import Tile1        from "./Tile1";
import Tile2        from "./Tile2";
import Tile3        from "./Tile3";
import TileBg1      from "./TileBg1";
import TileBg2      from "./TileBg2";

import isMobile     from "ismobilejs";

/**
 * @class
 */
export default class GameScene extends p3.Scene {
    constructor() {
        super();

        /**
         * @member {p3.Camera}
         * @private
         */
        this._camera = null;

        /**
         * @member {PIXI.Container}
         * @private
         */
        this._gameLayer = null;

        /**
         * @member {PIXI.Container}
         * @private
         */
        this._tileLayer = null;

        /**
         * @member {PIXI.Container}
         * @private
         */
        this._bgLayer = null;

        /**
         * @member {number}
         * @private
         */
        this._bgParallax = 0.6;

        /**
         * @member {number}
         * @private
         */
        this._bgParallaxInv = 1 / this._bgParallax;

        /**
         * @member {Replicator}
         * @private
         */
        this._replicator = null;

        /**
         * @member {Array.<Actor>}
         * @private
         */
        this._bgTiles = null;

        /**
         * @member {number}
         * @private
         */
        this._tileCount = 0;

        /**
         * @member {number}
         * @private
         */
        this._bgTileCount = 0;

        /**
         * @member {number}
         * @private
         */
        this._tileStartOffset = -530;

        /**
         * @member {number}
         * @private
         */
        this._bgTileStartOffset = 600;

        /**
         * @member {number}
         * @private
         */
        this._tileCreateOffset = 0;

        /**
         * @member {number}
         * @private
         */
        this._bgTileCreateOffset = 0;

        /**
         * @member {number}
         * @private
         */
        this._freezeOffset = 2;

        /**
         * @member {number}
         * @private
         */
        this._animateCameraCount = 2;

        /**
         * @member {number}
         * @private
         */
        this._increaseRangeCount = 5;

        /**
         * @member {Array.<Actor>}
         * @private
         */
        this._actors = [];

        /**
         * @member {Array.<Monkey>}
         * @private
         */
        this._monkeys = [];

        /**
         * @member {p2.Body}
         * @private
         */
        this._floorBody = null;

        /**
         * @member {p2.Body}
         * @private
         */
        this._wallBody = null;

        /**
         * @member {*}
         * @private
         */
        this._floorPosition = new PIXI.Point(p3.View.width * 0.5, 110);

        /**
         * @member {boolean}
         * @private
         */
        this._dropReady = true;

        /**
         * @member {boolean}
         * @private
         */
        this._towerStable = true;

        /**
         * @member {Monkey}
         * @private
         */
        this._topMonkey = null;

        /**
         * @member {Hud}
         * @private
         */
        this._hud = null;

        /**
         * @member {PIXI.Sprite}
         * @private
         */
        this._scoreTab = null;

        /**
         * @member {boolean}
         * @private
         */
        this._gameOver = false;

        /**
         * @member {number}
         * @private
         */
        this._score = 0;

        /**
         * @member {number}
         * @private
         */
        this._scoreRangeMin = 10;

        /**
         * @member {number}
         * @private
         */
        this._scoreRangeMax = 40;

        /**
         * @member {TimelineMax}
         * @private
         */
        this._replicatorAnim = null;

        /**
         * @member {TimelineMax}
         * @private
         */
        this._cameraAnim = null;

        /**
         * @member {TimelineMax}
         * @private
         */
        this._scoreTabAnim = null;

        /**
         * @type {Audio}
         * @private
         */
        this._ambientSFX = null;

        /**
         * @type {boolean}
         * @private
         */
        this._isHighscore = false;

        /**
         * @type {TweenMax}
         * @private
         */
        this._endDelay = null;
    }

    init() {
        super.init();

        this.createWorld();

        const assets = Common.assets;

        const bg = new PIXI.Sprite(assets.getTexture("bg_game"));
        this.addChild(bg);

        this._camera = new p3.Camera(new PIXI.Point(0, 1200));
        this._camera.trackEase = 0.04;
        Common.camera = this._camera;

        this._bgLayer = new PIXI.Container();
        this.addChild(this._bgLayer);

        this._tileLayer = new PIXI.Container();
        this.addChild(this._tileLayer);

        this._gameLayer = new PIXI.Container();
        this.addChild(this._gameLayer);

        const top = new PIXI.Sprite(assets.getTexture("gradient_top"));
        top.width = p3.View.width;
        this.addChild(top);

        const bottom = new PIXI.Sprite(assets.getTexture("gradient_btm"));
        bottom.y = Common.STAGE_HEIGHT;
        bottom.width = p3.View.width;
        bottom.anchor.y = 1;
        this.addChild(bottom);

        this._hit = new PIXI.Container();
        this._hit.hitArea = new PIXI.Rectangle();
        this.addChild(this._hit);

        this._hud = new Hud();
        this._hud.updateBestScore(Common.getHighscore());
        this._hud.pauseButton.signals.click.add(this.onPauseButtonClick, this);
        this.addChild(this._hud);

        this._camera.addLayer("game", this._gameLayer, new PIXI.Point(1, 1));
        this._camera.addLayer("tile", this._tileLayer, new PIXI.Point(1, 1));
        this._camera.addLayer("bg", this._bgLayer, new PIXI.Point(1, this._bgParallax));

        this._camera.trackTarget(new PIXI.Point(0, 0), true);

        const floor = new Actor(assets.getTexture("tile_000"));
        floor.y = this._tileStartOffset;
        floor.view.anchor = new PIXI.Point();
        this._tileLayer.addChild(floor);
        this._actors.push(floor);

        // first steve
        this._topMonkey = new Monkey();
        this._topMonkey.x = p3.View.width * 0.5;
        this._topMonkey.y = this._floorPosition.y - (this._topMonkey.body.shapes[0].height + this._floorBody.shapes[0].height) * 0.5;
        this._topMonkey.towerY = this._topMonkey.y - 10;
        this._topMonkey.body.type = p2.Body.KINEMATIC;
        this._gameLayer.addChild(this._topMonkey);
        this._monkeys.push(this._topMonkey);
        this._actors.push(this._topMonkey);

        this._replicator = new Replicator();
        this._replicator.x = p3.View.width * 0.5;
        this._replicator.y = -640;
        this._gameLayer.addChild(this._replicator);
        this._actors.push(this._replicator);

        this._scoreTab = new PIXI.Sprite(assets.getTexture("ui_score"));
        this._scoreTab.anchor = new PIXI.Point(0.0, 0.5);
        this._scoreTab.visible = false;
        this._gameLayer.addChild(this._scoreTab);

        this._scoreTab.label = new PIXI.extras.BitmapText("", {font: "60px Cloudy Bold", align: "center"});
        this._scoreTab.label.x = 72;
        this._scoreTab.label.pivot.y = this._scoreTab.label.textHeight * 0.5;
        this._scoreTab.addChild(this._scoreTab.label);

        this.updateScoreTab();

        this.createTile();
        this.createBgTile();

        this.playAmbientSFX();

        //this._isHighscore = this._score >= Common.getHighscore();
    }

    destroy() {
        TweenMax.killAll();
        Common.world.clear();

        for (let i = 0; i < this._monkeys.length; ++ i) {
            const monkey = this._monkeys[i];
            monkey.destroy();
        }
        this._monkeys.length = 0;

        this._replicator.destroy();

        this.stopAmbientSFX();

        super.destroy();
    }

    appear() {
        super.appear();

        this._hit.interactive = true;
        this._hit.click = this._hit.tap = this.onMouseClick.bind(this);

        Common.isFirstTime && this._hud.startIntroAnimation();
        Common.isFirstTime = false;

        delay(function() {
            this.signals.pause.dispatch(this);
        }, 0.5, this);
    }

    show() {
        this._hit.interactive = true;
        this._hud.pauseButton.interactive = true;

        this.resumeGame();
    }

    hide() {
        this._hit.interactive = false;
        this._hud.pauseButton.interactive = false;
        this._hud.pauseButton.onMouseOut();
    }

    /**
     * @param {function} [callback]
     * @param {*} [scope]
     */
    animateIn(callback, scope) {
        this._hud.bestScore.scale = new PIXI.Point();
        TweenMax.to(this._hud.bestScore.scale, 0.34, {
            delay: 0.1,
            x: 1,
            y: 1,
            ease: Back.easeOut,
            easeParams: [1],
            onStart: function() {
                this._hud.bestScore.visible = true;
            },
            onStartScope: this
        });

        this._hud.pauseButton.scale = new PIXI.Point();
        TweenMax.to(this._hud.pauseButton.scale, 0.34, {
            delay: 0.1 + 0.06,
            x: 1,
            y: 1,
            ease: Back.easeOut,
            easeParams: [1],
            onStart: function() {
                this._hud.pauseButton.visible = true;
            },
            onStartScope: this
        });
    }

    /**
     * @param {function} [callback]
     * @param {*} [scope]
     */
    animateOut(callback, scope) {
    }

    end() {
        this.signals.next.dispatch(this, this._score);

        Common.audio.playSound("sfx_game_over_00");

        console.log("gameover");
    }

    resize() {
        this.y = (p3.View.height - Common.STAGE_HEIGHT) * 0.5;
        this._hud.resize();

        this._hit.hitArea = new PIXI.Rectangle(
            0,
            (Common.STAGE_HEIGHT - p3.View.height) * 0.5 + 240,
            p3.View.width,
            p3.View.height - 240
        );
    }

    update() {
        if (Common.paused) return;

        Common.world.step(Common.PHYSICS_TICK);
        this._camera.update();

        this._hud.update();

        for (let i = this._actors.length - 1; i >= 0; -- i) {
            let actor = this._actors[i];
            if (actor.destroyNextFrame) {
                this._actors.splice(i, 1);
                actor.parent.removeChild(actor);
                actor.destroy(true);
            } else actor.update();
        }

        this.updateTiles();

        // move the walls with the camera
        this._wallBody.position[1] = this._camera.position.y + this._camera.view.y;

        if (!this._gameOver) {
            if (!this._towerStable) {
                const to = new p3.Vector2(this._replicator.x - this._topMonkey.x, this._replicator.y - this._topMonkey.y);
                if (to.lengthSq > (340 * 340) && this.isTowerStable()) {
                    this._topMonkey.towerY = this._topMonkey.y - 10;
                    this._towerStable = true;

                    this.freezeTower();

                    const value = this.calculateScore();
                    if (value == 10) {
                        this.createGummyExplosion(new PIXI.Point(this._topMonkey.x, this._topMonkey.y - 80));

                        Common.audio.playSound("sfx_steve_land_perfect_03");
                        delay(function() {
                            language == "en" && Common.audio.playSound("sfx_steve_gummybears_00");
                        }, 0.4, this);
                    }

                    Common.audio.playSound(["sfx_steve_land_normal_00", "sfx_steve_land_normal_01",
                        "sfx_steve_land_normal_02", "sfx_steve_land_normal_03", "sfx_steve_land_normal_04",]);

                    this._hud.animateScorePopup(value,
                        new PIXI.Point(this._topMonkey.x + 134, this._topMonkey.y - Common.camera.position.y));

                    this._score += value;

                    this.updateScoreTab();
                    this.animateScoreTab();

                    if (this._score > Common.getHighscore()) {
                        this._hud.updateBestScore(this._score);
                        if (!this._isHighscore) {
                            this._isHighscore = true;
                            this._hud.animateBestScore();

                            Common.audio.playSound("sfx_new_highscore_01");
                        }
                    }

                    this.animateReplicator(function() {
                        this._replicator.moving = true;
                        this._replicator.charged = true;

                        this._dropReady = true;
                        this._replicator.mixUpSpeed();

                        if (this._monkeys.length % this._increaseRangeCount == this._increaseRangeCount - 1) {
                            this._replicator.increaseSpeedRange();
                        }
                    }, this);

                    if (this._monkeys.length % this._animateCameraCount == this._animateCameraCount - 1) {
                        this.animateCamera();
                    }
                }
            }

            if (this.isTowerCollapsed()) {
                this._gameOver = true;

                this._endDelay && this._endDelay.kill();
                this._endDelay = delay(this.end, 1, this);

                Common.audio.playSound(["sfx_steve_fall_scream_00", "sfx_steve_fall_scream_01",
                    "sfx_steve_fall_scream_02", "sfx_steve_fall_scream_03"]);
            }
        }
    }

    updateTiles() {
        let y, tile;

        y = this._camera.target.y - this._camera.view.y - this._tileCreateOffset;
        if (y < 0) {
            this.createTile();
        }

        y = this._camera.target.y * this._bgParallax - this._camera.view.y - this._bgTileCreateOffset;
        if (y < 0) {
            this.createBgTile();
        }

        tile = this._tileLayer.children[0];
        if ((tile.y - this._camera.position.y) > Common.STAGE_HEIGHT) {
            this.destroyTile();
        }

        tile = this._bgLayer.children[0];
        if ((tile.y * this._bgParallaxInv - this._camera.position.y) > Common.STAGE_HEIGHT * this._bgParallaxInv) {
            this.destroyBgTile();
        }
    }

    createWorld() {
        const world = new p2.World({gravity: [0, 420]});
        world.on("beginContact", this.onBeginContact, this);
        world.on("endContact", this.onEndContact, this);
        Common.world = world;

        world.solver.iterations = 20;
        world.defaultContactMaterial.friction = 1;
        world.defaultContactMaterial.restitution = 0;
        world.sleepMode = p2.World.BODY_SLEEPING;

        world.addContactMaterial(new p2.ContactMaterial(PhysicsActor.MATERIAL_DEFAULT, PhysicsActor.MATERIAL_GUMMY_BEAR, {
            restitution: 0.8
        }));

        this._floorBody = new p2.Body({mass: 1});
        this._floorBody.type = p2.Body.STATIC;
        this._floorBody.position = [this._floorPosition.x, this._floorPosition.y];
        this._floorBody.parent = this;
        Common.world.addBody(this._floorBody);

        let shape;
        shape = new p2.Box({width: p3.View.width, height: 20});
        shape.collisionGroup = PhysicsActor.DEFAULT;
        shape.collisionMask = PhysicsActor.DEFAULT | PhysicsActor.GUMMY_BEAR;
        shape.material = PhysicsActor.MATERIAL_DEFAULT;
        this._floorBody.addShape(shape);
        this._floorBody.updateMassProperties();

        this._wallBody = new p2.Body({mass: 1});
        this._wallBody.type = p2.Body.STATIC;
        this._wallBody.position = [p3.View.width * 0.5, 0];
        this._wallBody.parent = this;
        Common.world.addBody(this._wallBody);

        shape = new p2.Box({width: 20, height: Common.STAGE_HEIGHT});
        shape.collisionGroup = PhysicsActor.DEFAULT;
        shape.collisionMask = PhysicsActor.GUMMY_BEAR;
        shape.material = PhysicsActor.MATERIAL_DEFAULT;
        this._wallBody.addShape(shape, [-p3.View.width * 0.5, 0]);

        shape = new p2.Box({width: 20, height: Common.STAGE_HEIGHT});
        shape.collisionGroup = PhysicsActor.DEFAULT;
        shape.collisionMask = PhysicsActor.GUMMY_BEAR;
        shape.material = PhysicsActor.MATERIAL_DEFAULT;
        this._wallBody.addShape(shape, [p3.View.width * 0.5, 0]);

        this._wallBody.updateMassProperties();
    }

    createTile() {
        const tiles = [Tile1, Tile2, Tile3];
        const tile = new (tiles[this._tileCount % tiles.length]);
        tile.y = this._tileStartOffset - (++ this._tileCount) * tile.view.texture.height;
        this._tileLayer.addChild(tile);
        this._actors.push(tile);

        this._tileCreateOffset = tile.y;
    }

    createBgTile() {
        const tiles = [TileBg1, TileBg2];
        const tile = new (tiles[this._bgTileCount % tiles.length]);
        tile.y = this._bgTileStartOffset - (++ this._bgTileCount) * tile.view.texture.height;
        this._bgLayer.addChild(tile);
        this._actors.push(tile);

        this._bgTileCreateOffset = tile.y;
    }

    destroyTile() {
        const tile = this._tileLayer.children[0];
        tile.destroyNextFrame = true;
    }

    destroyBgTile() {
        const tile = this._bgLayer.children[0];
        tile.destroyNextFrame = true;
    }

    /**
     * @param {boolean} [animate]
     * @returns {Monkey}
     */
    spawnMonkey(animate) {
        const top = this._monkeys[this._monkeys.length - 1];
        const monkey = new Monkey();
        monkey.x = this._replicator.x;
        monkey.y = this._replicator.y + 320;
        this._gameLayer.addChildAt(monkey, top.parent.getChildIndex(top));
        this._monkeys.push(monkey);
        this._actors.push(monkey);

        if (animate) {
            monkey.body.type = p2.Body.KINEMATIC;
            monkey.syncToBody = false;

            const duration = 0.26;
            const to = monkey.y;
            monkey.y -= 180;
            TweenMax.to(monkey, duration, {
                y: to,
                ease: Power1.easeOut,
                onComplete: function() {
                    monkey.body.type = p2.Body.DYNAMIC;
                    monkey.syncToBody = true;

                    Common.audio.playSound(language == "en" ? ["sfx_steve_00", "sfx_steve_01", "sfx_steve_can_00"] :
                        ["sfx_steve_00", "sfx_steve_01"]);
                },
                onCompleteScope: this
            });

            monkey.scale = new PIXI.Point(0.34, 0.34);
            TweenMax.to(monkey.scale, duration, {
                delay: 0.2,
                x: 1,
                ease: Back.easeOut,
                easeParams: [2]
            });
            TweenMax.to(monkey.scale, duration * 0.6, {
                delay: 0.2,
                y: 1,
                ease: Back.easeOut,
                easeParams: [2]
            });
        }
        return monkey;
    }

    /**
     * @param {function} [callback]
     * @param {*} [scope]
     */
    animateReplicator(callback, scope) {
        this._replicatorAnim = new TimelineMax({onComplete: callback, onCompleteScope: scope});

        const speed = 1100;
        const direction = Math.random() < 0.5 ? -1 : 1;
        const x = (this._replicator.startPos.x - (this._replicator.moveRange * direction));
        const dx = Math.abs(x - this._replicator.x);

        this._replicatorAnim.append(TweenMax.to(this._replicator, dx / speed, {
            x: x,
            ease: Power0.easeNone,
            onStart: function() {
                this._replicator.playMoveSFX();
            },
            onStartScope: this
        }));

        const y = this._replicator.y - this._topMonkey.body.shapes[0].height;
        const dy = Math.abs(y - this._replicator.y);
        this._replicatorAnim.append(TweenMax.to(this._replicator, dy / speed, {
            y: y,
            ease: Power0.easeNone,
            onStart: function() {
                this._replicator.playMoveUpSFX();
            },
            onStartScope: this
        }));
    }

    /**
     * @param {function} [callback]
     * @param {*} [scope]
     */
    animateCamera(callback, scope) {
        this._cameraAnim = new TimelineMax();
        this._cameraAnim.insert(TweenMax.to(this._camera.target, 0.4, {
            y: this._topMonkey.y,
            ease: Power2.easeInOut,
            onComplete: callback,
            onCompleteScope: scope
        }));
    }

    /**
     * @param {function} [callback]
     * @param {*} [scope]
     */
    animateScoreTab(callback, scope) {
        this._scoreTabAnim = new TimelineMax();

        if (!this._scoreTab.visible) {
            this._scoreTab.alpha = 0.0;
            this._scoreTabAnim.insert(TweenMax.to(this._scoreTab, 0.34, {
                alpha: 1.0,
                ease: Power1.easeInOut
            }));
        }

        this._scoreTabAnim.insert(TweenMax.to(this._scoreTab, 0.4, {
            y: this._topMonkey.y,
            ease: Back.easeOut,
            onStart: function() {
                this._scoreTab.visible = true;
            },
            onStartScope: this,
            onComplete: callback,
            onCompleteScope: scope
        }));
    }

    /**
     * @returns {number}
     */
    calculateScore() {
        let value = 0;
        if (this._monkeys.length > 1) {
            const head = this._monkeys[this._monkeys.length - 2];
            const dx = Math.abs(head.x - (this._topMonkey.x));
            value = Math.max(1, Math.floor((1 - Math.max(0, (dx - this._scoreRangeMin) / this._scoreRangeMax)) * 10));
        }
        return value;
    }

    updateScoreTab() {
        this._scoreTab.label.text = this._score.toString();
        this._scoreTab.label.validate();
        this._scoreTab.label.pivot.x = this._scoreTab.label.textWidth * 0.5;
    }

    freezeTower() {
        if (this._monkeys.length - this._freezeOffset > 0) {
            for (let i = 0; i < this._monkeys.length - this._freezeOffset; ++ i) {
                const body = this._monkeys[i].body;
                body.type = p2.Body.KINEMATIC;
            }
        }
    }

    isTowerStable() {
        let average = 0;
        let count = 0;
        for (let i = 0; i < this._monkeys.length; ++ i) {
            const monkey = this._monkeys[i];
            if (monkey.body.type == p2.Body.DYNAMIC) {
                average += monkey.body.velocity[0] * monkey.body.velocity[0] + monkey.body.velocity[1] * monkey.body.velocity[1];
                ++ count;
            }
        }
        average /= count;
        return average < 10;
    }

    isTowerCollapsed() {
        let result = false;
        if (this._monkeys.length > 1) {
            for (let i = 1; i < this._monkeys.length; ++ i) {
                const monkey = this._monkeys[i];
                let head = this._monkeys[i - 1];
                if (monkey.y > head.towerY) {
                    result = true;
                }
            }
        }
        return result;
    }

    /**
     * @param {PIXI.Point} [position]
     */
    createGummyExplosion(position) {
        position = position || PIXI.Point;

        for (let i = 0; i < 14; ++ i) {
            const force = new p3.Vector2();
            force.x = p3.Utils.randomRange(0.6, -0.6);
            force.y = p3.Utils.randomRange(-0.2, -0.6);

            const gummy = new GummyBear();
            gummy.x = position.x;
            gummy.y = position.y;
            gummy.rotation = Math.random() * (Math.PI * 2);
            this._gameLayer.addChildAt(gummy, this._scoreTab.parent.getChildIndex(this._scoreTab));
            this._actors.push(gummy);

            gummy.body.applyImpulse([force.x, force.y]);

            TweenMax.to(gummy.scale, 0.34, {
                delay: 1.4 + Math.random(),
                x: 0.0,
                y: 0.0,
                ease: Back.easeIn,
                easeParams: [2],
                onComplete: function(gummy) {
                    gummy.destroyNextFrame = true;
                },
                onCompleteParams: [gummy],
                onCompleteScope: this
            })
        }
    }

    resumeGame() {
        this._replicatorAnim && this._replicatorAnim.paused(false);
        this._cameraAnim && this._cameraAnim.paused(false);
        this._scoreTabAnim && this._scoreTabAnim.paused(false);
        this._endDelay && this._endDelay.paused(false);

        this._replicator.playMoveSFX();
        this.playAmbientSFX();
    }

    pauseGame() {
        this._replicatorAnim && this._replicatorAnim.paused(true);
        this._cameraAnim && this._cameraAnim.paused(true);
        this._scoreTabAnim && this._scoreTabAnim.paused(true);
        this._endDelay && this._endDelay.paused(true);

        this._replicator.stopMoveSFX();
        this.stopAmbientSFX();
    }

    playAmbientSFX() {
        this.stopAmbientSFX();

        const params = new AudioParams();
        params.loop = true;

        this._ambientSFX = Common.audio.playSound("sfx_ambience_lab_00", params);
    }

    stopAmbientSFX() {
        this._ambientSFX && this._ambientSFX.stop();
        this._ambientSFX = null;
    }

    /**
     * @param {p2.Event} event
     */
    onBeginContact(event) {
        const bodyA = event.bodyA;
        const bodyB = event.bodyB;

        if (bodyA.parent instanceof PhysicsActor && !bodyA.parent.hit) {
            bodyA.parent.onBeginContact(bodyB);
        }
        if (bodyB.parent instanceof PhysicsActor && !bodyB.parent.hit) {
            bodyB.parent.onBeginContact(bodyA);
        }
    }

    /**
     * @param {p2.Event} event
     */
    onEndContact(event) {
        const bodyA = event.bodyA;
        const bodyB = event.bodyB;

        if (bodyA.parent instanceof PhysicsActor && bodyA.parent.hit) {
            bodyA.parent.onEndContact(bodyB);
        }
        if (bodyB.parent instanceof PhysicsActor && bodyB.parent.hit) {
            bodyB.parent.onEndContact(bodyA);
        }
    }

    /**
     * @param {Event} event
     */
    onMouseClick(event) {
        if (!this._dropReady || this._gameOver) return;

        this._hud.stopIntroAnimation();

        this._topMonkey && (this._topMonkey.onTop = false);

        this._topMonkey = this.spawnMonkey(true);
        this._topMonkey.playSpawnState();

        this._replicator.animate();
        this._replicator.moving = false;
        this._replicator.charged = false;
        this._replicator.stopMoveSFX();

        this._dropReady = false;
        this._towerStable = false;

        Common.audio.playSound(["sfx_clone_zap_00", "sfx_clone_zap_01"]);
    }

    /**
     * @param {p3.Button} button
     */
    onPauseButtonClick(button) {
        this.signals.pause.dispatch(this);
    }
}
